import React, { useState } from 'react';
import { Box, InputBase, Typography } from '@mui/material';
import { ArrowForward, AutoAwesome, HelpOutline } from '@mui/icons-material';
import { AI_SUGGESTIONS } from './missionData';
import { s, cardSx, focusRing, reduceMotion } from './missionTokens';

const MODES = ['Ask', 'Analyze', 'Automate', 'Insights'] as const;
type Mode = (typeof MODES)[number];

/**
 * Stratify AI — the contextual intelligence surface, not a chat bubble bolted
 * into a corner.
 *
 * The four modes are the brief's capability split (ask / analyze / automate /
 * insights). Submitting hands the question to the existing global command
 * palette rather than answering inline: there must be exactly one place in the
 * product where a natural-language question is resolved, or the two drift.
 */
interface StratifyAiPanelProps {
  /** Called with the typed question. Wire to the Ctrl+K palette / AI route. */
  onAsk?: (question: string, mode: Mode) => void;
}

const StratifyAiPanel: React.FC<StratifyAiPanelProps> = ({ onAsk }) => {
  const [mode, setMode] = useState<Mode>('Ask');
  const [value, setValue] = useState('');

  const submit = (q: string) => {
    const question = q.trim();
    if (!question) return;
    onAsk?.(question, mode);
    setValue('');
  };

  return (
    <Box sx={{ ...cardSx, overflow: 'hidden' }}>
      {/* Header carries the only gradient on the page's right rail — it marks
          AI as a distinct surface without tinting the whole card. */}
      <Box
        sx={{
          position: 'relative',
          px: 2,
          pt: 2,
          pb: 1.75,
          background: 'linear-gradient(135deg, #eef2ff 0%, #f5f0ff 55%, #ffffff 100%)',
          borderBottom: `1px solid ${s.border}`,
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute', top: -40, right: -30, width: 150, height: 120,
            background: 'radial-gradient(circle, rgba(139,92,246,0.2), transparent 70%)',
          }}
        />
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.25 }}>
          <Box
            sx={{
              width: 34, height: 34, borderRadius: 2, display: 'grid', placeItems: 'center',
              background: `linear-gradient(135deg, ${s.violet}, ${s.primary})`,
              boxShadow: '0 6px 16px rgba(99,102,241,0.34)',
            }}
          >
            <AutoAwesome sx={{ fontSize: 18, color: '#fff' }} />
          </Box>
          <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: s.heading }}>Stratify AI</Typography>
          <Box sx={{ px: 0.75, py: 0.15, borderRadius: 1, backgroundColor: s.violetSoft }}>
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: s.violet, letterSpacing: '0.06em' }}>
              BETA
            </Typography>
          </Box>
        </Box>
        <Typography sx={{ position: 'relative', fontSize: '0.76rem', color: s.body }}>
          Your AI-powered business companion.
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
          {MODES.map((m) => (
            <Box
              key={m}
              component="button"
              onClick={() => setMode(m)}
              aria-pressed={mode === m}
              sx={{
                px: 1.5, py: 0.6, borderRadius: 5, cursor: 'pointer',
                border: `1px solid ${mode === m ? 'transparent' : s.border}`,
                backgroundColor: mode === m ? s.primary : s.paper,
                color: mode === m ? '#fff' : s.body,
                fontSize: '0.74rem',
                fontWeight: 700,
                transition: 'background-color .2s ease, color .2s ease',
                ...focusRing,
                ...reduceMotion,
              }}
            >
              {m}
            </Box>
          ))}
        </Box>

        <Box
          component="form"
          onSubmit={(e: React.FormEvent) => { e.preventDefault(); submit(value); }}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.5, mb: 1.5,
            borderRadius: 2, border: `1px solid ${s.border}`, backgroundColor: s.canvas,
            transition: 'border-color .2s ease, box-shadow .2s ease',
            '&:focus-within': { borderColor: s.primary, boxShadow: `0 0 0 3px ${s.primarySoft}` },
            ...reduceMotion,
          }}
        >
          <InputBase
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Ask anything about your business..."
            inputProps={{ 'aria-label': 'Ask Stratify AI about your business' }}
            sx={{ flex: 1, fontSize: '0.8rem', color: s.heading }}
          />
          <Box
            component="button"
            type="submit"
            aria-label="Submit question"
            sx={{
              width: 28, height: 28, borderRadius: 1.5, border: 0, cursor: 'pointer', flexShrink: 0,
              display: 'grid', placeItems: 'center',
              background: `linear-gradient(135deg, ${s.primary}, ${s.indigo})`,
              ...focusRing,
            }}
          >
            <ArrowForward sx={{ fontSize: 15, color: '#fff' }} />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {AI_SUGGESTIONS.map((q, i) => (
            <Box
              key={q}
              component="button"
              onClick={() => submit(q)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, width: '100%', textAlign: 'left',
                px: 1.25, py: 0.9, cursor: 'pointer', borderRadius: 2,
                border: `1px solid ${s.border}`, backgroundColor: s.paper,
                animation: `aiIn .4s ease ${i * 60}ms both`,
                '@keyframes aiIn': { from: { opacity: 0 }, to: { opacity: 1 } },
                transition: 'border-color .2s ease, background-color .2s ease',
                '&:hover': { borderColor: s.primary, backgroundColor: s.primarySoft },
                ...focusRing,
                ...reduceMotion,
              }}
            >
              <HelpOutline sx={{ fontSize: 15, color: s.muted, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.75rem', color: s.body, lineHeight: 1.35 }}>{q}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default StratifyAiPanel;
